/**
 * Camera (local video) subsystem for voice — extracted from voiceMedia's
 * controller so the god file stays focused on snapshot orchestration.
 * This owns the camera capture and toggle lifecycle, producing through the SFU.
 * It reaches back only through the narrow {@link CameraHost} interface
 * (closures, same pattern as SfuHost / ScreenShareHost).
 *
 * Behaviour is pinned by voiceMedia.media.test.ts (the SFU camera
 * produce/teardown case).
 */
import i18n from '@/i18n';
import { normalizeMediaError } from '@/lib/voice/voiceMediaHelpers';

/** What the camera session needs from the voice controller. Implemented with
 *  closures so the controller's internals stay private. */
export interface CameraHost {
  /** /api/rtc/config has answered — voice media may connect. */
  isMediaReady(): boolean;
  getActiveChannelId(): string | null;
  getLocalCameraStream(): MediaStream | null;
  /** Adopt a fresh capture into the snapshot (and clear any prior error). */
  onCameraStreamStarted(stream: MediaStream): void;
  /** Drop the capture from the snapshot. Called UNCONDITIONALLY by
   *  stopLocalStream(); the implementation must guard against "no stream set"
   *  so a redundant stop doesn't fire a spurious state emission. */
  onCameraStreamStopped(): void;
  produceVideo(channelId: string, track: MediaStreamTrack): Promise<void>;
  closeVideoProducer(channelId: string | null): Promise<void>;
}

export class VoiceCameraSession {
  constructor(private host: CameraHost) {}

  async setEnabled(enabled: boolean): Promise<boolean> {
    if (!enabled) {
      await this.disable();
      return true;
    }
    // Checked BEFORE capturing, so a refusal never leaves the camera light on.
    if (!this.host.isMediaReady()) throw new Error('media_not_ready');
    const stream = await navigator.mediaDevices
      .getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      })
      .catch((err) => {
        throw normalizeMediaError(err, 'camera');
      });
    const track = stream.getVideoTracks()[0] ?? null;
    if (!track) {
      for (const ownedTrack of stream.getTracks()) {
        ownedTrack.stop();
      }
      throw new Error(i18n.t('lib2.voice.noVideoTrack'));
    }
    this.stopLocalStream();
    this.host.onCameraStreamStarted(stream);
    track.onended = () => {
      void this.disable();
    };
    const channelId = this.host.getActiveChannelId();
    if (!channelId) throw new Error('sfu_not_in_channel');
    await this.host.produceVideo(channelId, track);
    return true;
  }

  async disable(): Promise<void> {
    await this.host.closeVideoProducer(this.host.getActiveChannelId());
    this.stopLocalStream();
  }

  stopLocalStream(): void {
    for (const track of this.host.getLocalCameraStream()?.getTracks() ?? []) {
      track.stop();
    }
    this.host.onCameraStreamStopped();
  }
}
